import {
    forwardRef, useState, useImperativeHandle, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'antd/lib/table/interface';
import {
    Alert, Button, Grid, Space,
} from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Table from '../table';
import { usePurchases } from '../../hooks/usePurchases';
import { useOrder } from '../../hooks/useOrder';
import { useFormWatchMode } from '../../hooks/useFormWatchMode';
import { useFetch } from '../../hooks/useFetch';
import { USER_ROLE_MODERATOR_LIMITED, useUser } from '../../hooks/useUser';

export interface IOrderPurchasesRef {
    selectedRowKeys: Key[];
    setSelectedRowKeys: (value: Key[]) => void;
}

interface IOrderPurchasesProps {
    warehouseId?: number;
}

function OrderPurchases({ warehouseId }: IOrderPurchasesProps, ref: any) {
    const fetch = useFetch();
    const order = useOrder();
    const { data, loading } = usePurchases({
        canFetch: typeof warehouseId === 'number',
        inOrderForm: true,
        orderId: order.data?.id,
        warehouseId,
    });
    const { i18n, t } = useTranslation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const { watchMode } = useFormWatchMode();
    const { md } = Grid.useBreakpoint();
    const [dutyMessage, setDutyMessage] = useState<string>();
    const user = useUser({ canFetch: false, canLogoutBy401: false, showFetchMessages: false });

    const limitedAccess = user.info?.role_value === USER_ROLE_MODERATOR_LIMITED;

    const sum = data?.list?.reduce((val, row) => {
        if (selectedRowKeys.length > 0 && selectedRowKeys.includes(row.id)) {
            val += row.sum_value;
        }
        return val;
    }, 0) || 0;

    const validateDutySum = async () => {
        if (sum > 0) {
            const response = await fetch.request({
                url: '/orders/validate-duty-sum',
                method: 'post',
                data: {
                    from: 'DE',
                    sum,
                },
            });
            setDutyMessage(response.success ? response.message : undefined);
        } else {
            setDutyMessage(undefined);
        }
    };

    useEffect(() => {
        validateDutySum();
    }, [i18n.language, sum]);

    useImperativeHandle(ref, () => ({
        selectedRowKeys,
        setSelectedRowKeys,
    }));

    const totalSum = () => (
        <span className="order-items-sum">{`${t('sum')}: ${sum}€`}</span>
    );

    return (
        <Space className="w-100" direction="vertical" size="large">
            {order.data instanceof Object && order.data.warehouse_id && !limitedAccess && (
                <Link to={`/purchases/create?order_id=${order.data.id}&warehouse_id=${order.data.warehouse_id}`}>
                    <Button
                        icon={<PlusOutlined />}
                        type="primary"
                        style={md ? undefined : { height: 48, width: 48, padding: 0 }}
                    >
                        {md && <span>{t('addPurchase')}</span>}
                    </Button>
                </Link>
            )}
            <Table
                scroll={{ x: true }}
                rowKey="id"
                loading={loading}
                dataSource={data?.list}
                pagination={watchMode ? false : {
                    current: data?.currentPage,
                    total: data?.total,
                    pageSize: data?.perPage,
                    showTotal: sum > 0 ? totalSum : undefined,
                }}
                columns={[{
                    dataIndex: 'name',
                    title: t('goodName'),
                }, {
                    dataIndex: 'shop_name',
                    title: t('shopName'),
                }, {
                    dataIndex: 'warehouse',
                    title: t('warehouse'),
                }, {
                    dataIndex: 'tracking_code',
                    title: t('trackingNumber'),
                }, {
                    dataIndex: 'sum',
                    title: t('sum'),
                }, {
                    render: (row) => (
                        <Link to={`/purchases/${row.id}`}>
                            <Button icon={<EditOutlined />} type="primary" />
                        </Link>
                    ),
                    width: 50,
                }]}
            />
            {typeof dutyMessage === 'string' && (
                <Alert className="order-items-sum-alert" message={dutyMessage} />
            )}
        </Space>
    );
}

export default forwardRef(OrderPurchases);
